import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { ArrowLeft, Syringe, FileText, Calendar, Download } from "lucide-react";

async function downloadCompleteRecord(petId, petName) {
  const tid = toast.loading("Preparing complete record…");
  try {
    const res = await axios.get(`${API}/pets/${petId}/complete-record`, {
      responseType: "blob",
      withCredentials: true,
    });
    if (res.data?.type?.includes("application/json")) {
      const txt = await res.data.text();
      throw new Error(txt || "Download failed");
    }
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    a.href = url;
    a.download = `PetBond_${(petName || "Pet").replace(/\s+/g, "_")}_Complete_Record_${today}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Complete record downloaded", { id: tid });
  } catch (e) {
    toast.error(e?.response?.status === 404 ? "Pet not found" : (e?.message || "Download failed"), { id: tid });
  }
}

export default function PetMedicalHistory() {
  const { petId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/pets/${petId}/medical-history`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Could not load medical history"));
  }, [petId]);

  if (err) return (
    <div className="space-y-4" data-testid="medical-history-error">
      <Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back to my pets</Link>
      <div className="bg-surface border border-line rounded-3xl p-8 text-center text-danger font-bold">{err}</div>
    </div>
  );
  if (!data) return <div className="text-ink-soft">Loading…</div>;
  const { pet, prescriptions = [], vaccinations = [] } = data;

  return (
    <div className="space-y-6" data-testid="medical-history-page">
      <Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back to my pets</Link>

      <header className="bg-surface border border-line rounded-3xl p-5 shadow-soft flex flex-wrap items-center gap-4">
        <img src={pet.photo} alt={pet.name} className="w-20 h-20 rounded-2xl object-cover"/>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-3xl font-extrabold">{pet.name}&apos;s medical record</h1>
          <div className="text-sm text-ink-soft">{pet.breed} · {pet.age_years}y · {pet.species}</div>
        </div>
        <button
          type="button"
          data-testid="download-complete-record"
          onClick={() => downloadCompleteRecord(pet.pet_id, pet.name)}
          className="rounded-full px-4 py-2.5 text-sm font-bold border inline-flex items-center gap-2 transition-colors bg-ink text-white border-ink hover:bg-brand hover:border-brand"
          title="Download a PDF with the pet's complete record — profile, vaccinations, appointments, Rx, licence, insurance, lost-pet events"
        >
          <Download className="w-4 h-4"/>Download complete record
        </button>
      </header>

      <section className="grid lg:grid-cols-2 gap-5">
        {/* Prescriptions */}
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="rx-section">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-brand"/> Prescriptions ({prescriptions.length})</h2>
          {prescriptions.length === 0 ? (
            <div className="text-ink-soft text-sm">No prescriptions yet. Your vet's notes will appear here after each visit.</div>
          ) : (
            <ul className="space-y-2">
              {prescriptions.map(rx => (
                <li key={rx.prescription_id} data-testid={`rx-${rx.prescription_id}`}>
                  <Link to={`/app/prescription/${pet.pet_id}/${rx.prescription_id}`} className="block bg-surface-muted hover:bg-brand/10 transition-colors rounded-2xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold truncate">{rx.diagnosis}</div>
                        <div className="text-xs text-ink-soft">
                          {new Date(rx.created_at).toLocaleDateString()} · {rx.items?.length || 0} medicine(s) · {rx.vet_name || "Vet"}
                        </div>
                      </div>
                      <span className="text-xs font-bold text-brand whitespace-nowrap">View →</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Vaccinations */}
        <div className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="vac-section">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-3"><Syringe className="w-5 h-5 text-sky"/> Vaccinations ({vaccinations.length})</h2>
          {vaccinations.length === 0 ? (
            <div className="text-ink-soft text-sm">No vaccinations recorded yet.</div>
          ) : (
            <ul className="space-y-2">
              {vaccinations.map(v => (
                <li key={v.record_id || `${v.name}-${v.date}`} className="bg-surface-muted rounded-2xl p-3" data-testid={`vac-${v.record_id || v.name}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-bold">{v.name}</div>
                      <div className="text-xs text-ink-soft">Given {v.date} {v.manufacturer && <>· {v.manufacturer}</>} {v.batch_number && <>· Batch {v.batch_number}</>}</div>
                      {v.notes && <div className="text-xs text-ink-soft mt-1 italic">{v.notes}</div>}
                    </div>
                    {v.next_due && (
                      <span className="bg-sunshine/30 text-ink rounded-full px-3 py-1 text-xs font-bold inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3"/> Next: {v.next_due}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
